import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccess } from "@/contexts/AccessContext";

function StatusMessage({ title, subtitle }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-6">
      <div className="text-lg font-semibold mb-2">{title}</div>
      {subtitle ? <div className="text-sm text-gray-300 max-w-md text-center">{subtitle}</div> : null}
    </div>
  );
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export default function StripeReturnPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAccess } = useAccess() || {};
  const ranOnceRef = useRef(false);

  const [status, setStatus] = useState("finalising"); // finalising | error
  const [error, setError] = useState("");

  // Read studioId from URL query params or localStorage
  const studioId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('studioId');
    if (fromUrl) return fromUrl;
    
    // Fallback to localStorage
    try {
      return localStorage.getItem('mudo:stripe_onboarding_studioId') || null;
    } catch {
      return null;
    }
  }, [location.search]);

  const returnPath = useMemo(() => {
    const isCeramics = String(location?.pathname || "").startsWith("/ceramics/");
    const prefix = isCeramics ? "/ceramics" : "";
    return `${prefix}/dashboard/home?stripe=done`;
  }, [location?.pathname]);

  const run = useCallback(async () => {
    setStatus("finalising");
    setError("");

    if (!studioId) {
      throw new Error("Invalid or missing studioId");
    }

    // Call public finalize endpoint (no auth required)
    // This endpoint uses server-side Supabase admin to update studio record
    const res = await fetch(`/api/stripe/finalize-public?studioId=${encodeURIComponent(studioId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ studioId }),
    });

    const data = await readJsonSafe(res);
    
    if (!res.ok || !data?.ok) {
      throw new Error(
        data?.error || 
        data?.message || 
        `Failed to finalise Stripe (${res.status})`
      );
    }

    // Clear localStorage after successful completion
    try {
      localStorage.removeItem('mudo:stripe_onboarding_studioId');
    } catch {}

    navigate(returnPath, { replace: true });
  }, [navigate, returnPath, studioId]);

  useEffect(() => {
    if (ranOnceRef.current) return;
    ranOnceRef.current = true;
    
    (async () => {
      try {
        await run();
        // Refresh access after Stripe setup completes (ignore errors)
        try {
          await refreshAccess?.();
        } catch (refreshErr) {
          console.warn("[StripeReturnPage] refreshAccess failed (non-fatal)", {
            error: refreshErr?.message
          });
        }
      } catch (e) {
        setError(e?.message || "Failed to finalise Stripe.");
        setStatus("error");
      }
    })();
  }, [run, refreshAccess]);

  if (status === "finalising") {
    return <StatusMessage title="Finalising Stripe…" subtitle="Please wait a moment while we sync your Stripe setup." />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-6 gap-4">
      <div className="text-lg font-semibold">Stripe finalisation failed</div>
      {error ? <div className="text-sm text-red-300 max-w-md text-center">{error}</div> : null}
      <div className="flex gap-3">
        <button
          onClick={() => run().catch(() => {})}
          className="px-4 py-2 rounded-md bg-white text-black font-semibold hover:bg-gray-200"
        >
          Try again
        </button>
        <button
          onClick={() => navigate(returnPath, { replace: true })}
          className="px-4 py-2 rounded-md bg-gray-800 text-white font-semibold hover:bg-gray-700"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
}

